import {
    useState,
    useEffect
} from 'react';
import useScreen from 'Hooks/useScreen';
import { ButtonsContainer } from 'components/shareable/Modal/styles';
import { useHistory } from 'react-router-dom';
import CardOption from 'components/shareable/Modal/CardOption';
import RecordSearchView from 'elements/RecordSearchView';
import { track } from 'analytics';
import { clsx, isValidURL, makeKey, generateShortUniqueId, calculateThemeColors } from "modules/Utils"
import { RecordDetails } from 'modules/Types';
import { WebsiteBuilderTemplateType, WebsiteType } from 'modules/Const';
import ButtonSymphony from 'elements/ButtonSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import useCurrentBrand, { BrandExistsState } from 'Hooks/useCurrentBrand';
import useWebsiteBuilder, { WebsiteBuilderTemplate } from 'Hooks/useWebsiteBuilder';
import { getPalette } from 'react-palette';

export enum CreateNewWebsiteSteps {
    SELECT_OPTION = "SELECT_OPTION",
    SELECT_RECORD = "SELECT_RECORD",
}

interface CreateWebsiteOptionsProps {
    step: CreateNewWebsiteSteps;
    setStep: (step: CreateNewWebsiteSteps) => void;
}

export const CreateWebsiteOptions = ({
    step,
    setStep
}: CreateWebsiteOptionsProps) => {
    const history = useHistory()
    const { mobileView, cssCalc } = useScreen();
    const {
        currentBrand,
    } = useCurrentBrand() as BrandExistsState;

    const {
        getTemplate,
        setShowCreateNewWebsiteModal,
        setWebsiteContent,
        setWebsiteStyle,
        getInitialWebsiteStyle,
        websiteStyle,
    } = useWebsiteBuilder();

    const websiteSlug = generateShortUniqueId()

    const handleSetWebsiteTemplate = async (websiteTemplate: WebsiteBuilderTemplate) => {
        const {
            websiteContent,
        } = websiteTemplate

        if (websiteTemplate.type === WebsiteBuilderTemplateType.MUSIC_1) {
            await getInitialWebsiteStyle(websiteContent[0].musicContent?.selectedMusic?.thumbnail_url)
        } else {
            await getInitialWebsiteStyle()
        }

        setWebsiteContent(websiteContent)
    }

    const CREATE_NEW_WEBSITE_CARDS = [
        {
            thumbnail: 'bulletList',
            text: "Build a Link In Bio",
            subtitle: "Build a dynamic link in bio for your social media accounts.",
            onClick: () => {
                track('Start "New Website" flow', { type: 'websiteBuilder' })
                const websiteTemplate = getTemplate({
                    templateType: WebsiteBuilderTemplateType.BASIC
                })

                handleSetWebsiteTemplate(websiteTemplate!)

                history.push(`/website-builder/${currentBrand.slug}/${websiteSlug}`)
            }
        },
        {
            name: 'musicSite',
            thumbnail: 'musicSite',
            text: "Promote your Songs, Projects, and Playlists",
            subtitle: "Showcase your latest releases with our music site - perfect for promoting your songs, albums, and playlists across streaming platforms.",
            onClick: () => {
                track('Start "New Website" flow', { type: 'music' })
                // NOTE: go to handleSelectRecord() in this file to see what happens when a record is selected
                setStep(CreateNewWebsiteSteps.SELECT_RECORD)
            }
        },
        {
            name: 'dataCollectorSite',
            thumbnail: 'dataCollectorSite',
            text: "Collect Fan Data",
            subtitle: "Build your email + phone number list with our data collection toolset - perfect for fan clubs, drops, RSVPs, and anything in between.",
            onClick: () => {
                track('Start "New Website" flow', { type: 'data collector' })
                const websiteTemplate = getTemplate({
                    templateType: WebsiteBuilderTemplateType.COLLECT_DATA_1,
                })

                handleSetWebsiteTemplate(websiteTemplate!)

                history.push(`/website-builder/${currentBrand.slug}/${websiteSlug}`)
            }
        },
        {
            name: 'tourSite',
            thumbnail: 'tourSite',
            text: "Promote Events",
            subtitle: "Showcase your latest event or tour with our events site.",
            onClick: () => {
                track('Start "New Website" flow', { type: 'tour' })
                const websiteTemplate = getTemplate({
                    templateType: WebsiteBuilderTemplateType.TOUR_1,
                })

                handleSetWebsiteTemplate(websiteTemplate!)

                history.push(`/website-builder/${currentBrand.slug}/${websiteSlug}`)
            }
        },
        {
            name: 'presaveSite',
            thumbnail: 'presaveSite',
            text: "Create a Pre-Save",
            subtitle: "Build hype before your next release with Symphony's pre-save playbook.",
            onClick: () => {
                track('Start "New Website" flow', { type: 'pre-save' })
                const websiteTemplate = getTemplate({
                    templateType: WebsiteBuilderTemplateType.PRESAVE,
                })

                handleSetWebsiteTemplate(websiteTemplate!)

                history.push(`/marketing/new/presave`)
            }
        },
    ]

    const handleBack = () => {
        setStep(CreateNewWebsiteSteps.SELECT_OPTION)
    }

    const handleSelectRecord = async (recordDetails: RecordDetails, inputValue?: string) => {
        const websiteTemplate = getTemplate({
            templateType: WebsiteBuilderTemplateType.MUSIC_1,
            recordDetails,
        })

        handleSetWebsiteTemplate(websiteTemplate!)

        if (inputValue && isValidURL(inputValue)) {
            const url = encodeURIComponent(inputValue)
            // history.push(`/website/record_streams/${recordSlug}?url=${url}`)
            history.push(`/website-builder/${currentBrand.slug}/${websiteSlug}?url=${url}`)
        } else {
            // history.push(`/website/record_streams/${recordSlug}`)
            history.push(`/website-builder/${currentBrand.slug}/${websiteSlug}`)
        }

        const image = recordDetails.thumbnail_url;
        const paletteData = await getPalette(image);
        const {
            backgroundColor,
            secondaryColor,
            textColor
        } = calculateThemeColors(paletteData);

        setWebsiteStyle({
            ...websiteStyle,
            backgroundColor,
            secondaryColor,
            textColor,
        });

        setShowCreateNewWebsiteModal(false)
        setStep(CreateNewWebsiteSteps.SELECT_OPTION)
    }

    // NOTE: render()
    return (
        <>
            {step === CreateNewWebsiteSteps.SELECT_OPTION &&
                <div
                    className="createNewSiteModalInnerContainerSelectOption"
                    style={{
                        display: 'flex',
                        gap: '12px',
                        flexDirection: 'column',
                        width: '100%'
                    }}
                >
                    {CREATE_NEW_WEBSITE_CARDS.map((option, index) => (
                        <CardOption
                            key={makeKey('createSiteOption', index)}
                            className="createSiteOptionCreateNewSiteModal"
                            thumbnail={option.thumbnail}
                            text={option.text}
                            subtitle={option.subtitle}
                            onClick={() => {
                                if (option.onClick) {
                                    option.onClick()
                                }
                                if (option.name !== 'musicSite') {
                                    setShowCreateNewWebsiteModal(false)
                                }
                            }}
                        />
                    ))}
                </div>
            }
            {step === CreateNewWebsiteSteps.SELECT_RECORD &&
                <div
                    className="createNewSiteModalInnerContainerSelectRecord"
                    style={{
                        display: 'flex',
                        gap: 8,
                        flexDirection: 'column',
                        width: '100%'
                    }}
                >
                    <RecordSearchView
                        letUserSearchSpotify
                        handleSelectRecord={handleSelectRecord}
                        options={['single', 'project', 'playlist']}
                        maxHeight={mobileView ? cssCalc(410) : cssCalc(600)}
                    />
                    <ButtonsContainer
                        flexDirection={mobileView ? "column-reverse" : "row"}
                    >
                        <ButtonSymphony
                            className="createNewSiteModalBackButton"
                            onClick={handleBack}
                            width={mobileView ? "100%" : "fit-content"}
                            variant="outlined"
                        >
                            Back
                        </ButtonSymphony>
                    </ButtonsContainer>
                </div>
            }
        </>
    )
}

interface CreateNewWebsiteModalProps {
    className?: string;
    isFirstSite?: boolean;
}

// NOTE: component()
const CreateNewWebsiteModal = ({
    className,
    isFirstSite,
}: CreateNewWebsiteModalProps) => {
    const history = useHistory();
    const { mobileView } = useScreen();
    const {
        showCreateNewWebsiteModal,
        setShowCreateNewWebsiteModal,
        initialWebsiteType,
        resetWebsiteBuilder
    } = useWebsiteBuilder();

    const [step, setStep] = useState<CreateNewWebsiteSteps>(CreateNewWebsiteSteps.SELECT_OPTION);

    const onClose = () => {
        setStep(CreateNewWebsiteSteps.SELECT_OPTION);
        // resetWebsiteBuilder();
        setShowCreateNewWebsiteModal(false);
    }

    // NOTE: this should be moved to the useWebsiteBuilder hook
    // however, once templates is a thing, this will be deleted
    const handleInitialSiteType = (siteType: WebsiteType) => {
        switch (siteType) {
            case WebsiteType.RECORD_STREAMS:
                setStep(CreateNewWebsiteSteps.SELECT_RECORD);
                break;
            case WebsiteType.DATA_COLLECTOR:
                track('Start "New Website" flow', { type: 'data collector' });
                const dataCollectorSlug = generateShortUniqueId();
                history.push(`/website/data_collector/${dataCollectorSlug}`);
                onClose();
                break;
            case WebsiteType.TOUR:
                track('Start "New Website" flow', { type: 'tour' });
                const tourSlug = generateShortUniqueId();
                history.push(`/website/tour/${tourSlug}`);
                onClose();
                break;
            case WebsiteType.PRESAVE:
                track('Start "New Website" flow', { type: 'pre-save' });
                history.push('/marketing/new/presave');
                onClose();
                break;
        }
    };

    useEffect(() => {
        if (initialWebsiteType) {
            handleInitialSiteType(initialWebsiteType);
        }
    }, [initialWebsiteType]);

    let titleText = 'Create a New Website'
    if (isFirstSite) {
        titleText = 'Create your first site'
    }

    if (step === CreateNewWebsiteSteps.SELECT_RECORD) {
        titleText = "Create a Music Site"
    }

    let subtitleText = "Select a website based on your marketing goal - every site is hyper-optimized to help you grow your audience and track your data."
    if (step === CreateNewWebsiteSteps.SELECT_RECORD) {
        subtitleText = "Search for your release or paste a Spotify URL to create your website."
    }

    // NOTE: render()
    return (
        <ModalSymphony
            className={clsx('createNewWebsiteModal', className)}
            fullScreen={mobileView}
            isOpen={showCreateNewWebsiteModal}
            onClose={onClose}
            title={titleText}
            subtitle={subtitleText}
            headerIcon="mobile"
        >
            <CreateWebsiteOptions
                step={step}
                setStep={setStep}
            />
        </ModalSymphony>
    )
}

export default CreateNewWebsiteModal